import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import CompleteForm from "./components/CompleteForm";

interface MemberDetails {
  full_name: string;
  profession: string;
  city_state: string;
  description: string;
  whatsapp: string;
  country_code: string;
  email: string;
  bio_long: string;
  skills: string;
  languages: string;
  education: string;
  services: string;
  linkedin: string;
  facebook: string;
  tiktok: string;
  instagram: string;
  website: string;
  experience_years: string;
  photo: string;
  photo_position: string;
  has_physical_business: boolean;
  business_name: string;
  business_address: string;
  business_city: string;
  business_state: string;
  business_zip: string;
}

export default function CompleteRegistration() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [validating, setValidating] = useState(true);
  const [valid, setValid] = useState(false);
  const [memberName, setMemberName] = useState("");
  const [memberId, setMemberId] = useState<number | null>(null);
  const [initialData, setInitialData] = useState<MemberDetails | undefined>(undefined);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("Link inválido. Certifique-se de usar o link enviado por email.");
      setValidating(false);
      return;
    }

    async function validateToken() {
      try {
        const { data: r } = await supabase.rpc("registration_profile_load", { p_token: token });

        if (!r || r.status === "token_invalido") {
          setError("Link inválido ou expirado. Se você já foi aprovada, entre em contato com a administradora.");
          setValidating(false);
          return;
        }

        if (r.status === "pendente") {
          setError("Seu cadastro ainda não foi aprovado. Aguarde o email de confirmação.");
          setValidating(false);
          return;
        }

        if (r.status === "recusado") {
          setError("Seu cadastro não foi aprovado. Entre em contato com a administradora para mais informações.");
          setValidating(false);
          return;
        }

        // Já foi promovida pra active dentro da função — só abre a tela de concluído.
        if (r.status === "ativada") {
          setMemberId(r.id);
          setMemberName(r.full_name);
          setDone(true);
          setValid(true);
          return;
        }

        // status === "ok": pode completar/editar o perfil.
        setMemberId(r.id);
        setMemberName(r.dados.full_name);

        // Se já estiver "active", está editando — preenche com dados atuais
        if (r.member_status === "active") {
          setInitialData({
            full_name: r.dados.full_name || "",
            profession: r.dados.profession || "",
            city_state: r.dados.city_state || "",
            description: r.dados.description || "",
            whatsapp: r.dados.whatsapp || "",
            country_code: r.dados.country_code || "+55",
            email: r.dados.email || "",
            bio_long: r.dados.bio_long || "",
            skills: r.dados.skills || "",
            languages: r.dados.languages || "",
            education: r.dados.education || "",
            services: r.dados.services || "",
            linkedin: r.dados.linkedin || "",
            facebook: r.dados.facebook || "",
            tiktok: r.dados.tiktok || "",
            instagram: r.dados.instagram || "",
            website: r.dados.website || "",
            experience_years: r.dados.experience_years || "",
            photo: r.dados.photo || "",
            photo_position: r.dados.photo_position || "top",
            has_physical_business: r.dados.has_physical_business || false,
            business_name: r.dados.business_name || "",
            business_address: r.dados.business_address || "",
            business_city: r.dados.business_city || "",
            business_state: r.dados.business_state || "",
            business_zip: r.dados.business_zip || "",
          });
        }

        setValid(true);
      } catch {
        setError("Erro ao validar o link. Tente novamente mais tarde.");
      }
      setValidating(false);
    }

    validateToken();
  }, [token]);

  if (done) {
    return (
      <div className="min-h-screen bg-beige flex items-center justify-center px-6">
        <div className="w-full max-w-lg text-center">
          <div className="bg-white rounded-2xl p-10 md:p-14">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-primary/10 mx-auto mb-6">
              <i className="ri-check-double-line text-2xl text-primary" />
            </div>
            <h2 className="font-display text-2xl text-brown mb-3">
              Perfil {initialData ? "atualizado" : "completo"}!
            </h2>
            <p className="font-body text-brown/70 text-base leading-relaxed mb-8">
              {initialData
                ? "Suas alterações foram salvas com sucesso. Seu perfil já está atualizado na comunidade."
                : <>Seu perfil foi atualizado com todas as informações. Agora você faz parte oficialmente da comunidade Entre Elas.</>
              }
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/comunidade"
                className="inline-block font-body text-sm px-8 py-3 rounded-full bg-primary text-white tracking-wide uppercase transition-all duration-300 hover:bg-primary-dark whitespace-nowrap"
              >
                Ver comunidade
              </Link>
              <Link
                to={`/perfil/${memberId}`}
                className="inline-block font-body text-sm px-8 py-3 rounded-full border border-nude text-brown tracking-wide uppercase transition-all duration-300 hover:bg-beige whitespace-nowrap"
              >
                Ver meu perfil
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-beige">
      <div className="pt-24 pb-16 md:pt-32 md:pb-24">
        {validating ? (
          <div className="flex items-center justify-center py-32">
            <div className="text-center">
              <div className="w-10 h-10 border-2 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-4" />
              <p className="font-body text-sm text-brown/60">Validando seu link...</p>
            </div>
          </div>
        ) : error ? (
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-lg mx-auto text-center">
            <div className="bg-white rounded-2xl p-10 md:p-14">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-red-50 mx-auto mb-6">
                <i className="ri-error-warning-line text-2xl text-red-400" />
              </div>
              <h2 className="font-display text-xl text-brown mb-3">Ops!</h2>
              <p className="font-body text-brown/70 text-base leading-relaxed mb-6">{error}</p>
              <Link
                to="/"
                className="inline-block font-body text-sm px-6 py-3 rounded-full border border-nude text-brown tracking-wide transition-all duration-300 hover:bg-beige whitespace-nowrap"
              >
                Voltar para o início
              </Link>
            </div>
          </div>
        ) : valid && memberId ? (
          <CompleteForm
            memberId={memberId}
            memberName={memberName}
            approvalToken={token!}
            initialData={initialData}
            onComplete={() => setDone(true)}
          />
        ) : null}
      </div>
    </div>
  );
}